/**
 * Presentation helpers for the GCP Monitoring page.
 *
 * These exist because the raw shapes coming back from Cloud Monitoring are
 * accurate but unreadable: utilization arrives as a 0-1 fraction, delays as
 * raw seconds (a queue sitting at 26488 tells you nothing until it reads
 * "7h 21m"), and revisions as opaque names like `client-00454-god`. Every
 * helper here is pure so it can be reasoned about without the charts.
 */

/** 0.82 → "82%" */
export const formatPercent = (fraction) => {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) {
    return "—";
  }

  return `${Math.round(fraction * 100)}%`;
};

/**
 * Seconds → the largest two units that matter, e.g. 26488 → "7h 21m", 95 →
 * "1m 35s". Cloud Tasks delays routinely span seconds to hours, so a single
 * fixed unit is unreadable at one end or the other.
 */
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return "—";
  }

  const total = Math.round(seconds);
  if (total < 60) {
    return `${total}s`;
  }

  const minutes = Math.floor(total / 60);
  if (minutes < 60) {
    const remainder = total % 60;
    return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remainder = minutes % 60;
    return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainder = hours % 24;

  return remainder ? `${days}d ${remainder}h` : `${days}d`;
};

/** 1234 → "1.2k", so wide count columns stay narrow. */
export const formatCount = (value) => {
  const count = Math.round(value || 0);
  if (count < 1000) {
    return String(count);
  }
  if (count < 1_000_000) {
    return `${(count / 1000).toFixed(count < 10_000 ? 1 : 0)}k`;
  }

  return `${(count / 1_000_000).toFixed(1)}M`;
};

/**
 * Strip the service prefix Cloud Run bakes into revision names, so
 * `client-00454-god` reads as `00454-god` once the service is already shown
 * in its own column.
 */
export const shortRevision = (service, revision) => {
  if (!revision) {
    return "unknown";
  }
  if (service && revision.startsWith(`${service}-`)) {
    return revision.slice(service.length + 1);
  }

  return revision;
};

/**
 * Plain-language read of a memory trend. `projectedMinutesToLimit` is only
 * populated by the backend when the slope is real and the projection is near
 * enough to act on, so it takes priority when present.
 */
export const describeMemoryTrend = ({
  trendPerMinute,
  projectedMinutesToLimit,
}) => {
  if (projectedMinutesToLimit !== null && projectedMinutesToLimit !== undefined) {
    return {
      label: `hits limit in ~${formatDuration(projectedMinutesToLimit * 60)}`,
      tone: "critical",
    };
  }
  if ((trendPerMinute || 0) >= 0.002) {
    return { label: "climbing", tone: "warning" };
  }
  if ((trendPerMinute || 0) <= -0.002) {
    return { label: "falling", tone: "ok" };
  }

  return { label: "steady", tone: "neutral" };
};

/**
 * Turn a queue's numbers into one honest verdict.
 *
 * The thresholds mirror `QUEUE_THRESHOLDS` in the backend's
 * `monitoringConfig.js` so the panel and the ranked findings never disagree.
 * The low-volume case matters most for readability: a queue with 2 attempts
 * that both failed is "100% retry", which looks alarming next to a queue with
 * 20000 attempts at 60%. The backend already ignores those for findings
 * (`minAttemptsForRetryFinding`), and this says so out loud.
 */
export const describeQueue = (queue) => {
  const attempts = queue.totalAttempts || 0;
  const retryShare = queue.retryShare || 0;
  const depth = queue.currentDepth || 0;

  if (depth >= 2000) {
    return { label: "backlog critical", tone: "critical" };
  }
  if (attempts >= 20 && retryShare >= 0.6) {
    return { label: "mostly failing", tone: "critical" };
  }
  if (depth >= 500) {
    return { label: "backlog building", tone: "warning" };
  }
  if (attempts >= 20 && retryShare >= 0.3) {
    return { label: "retrying heavily", tone: "warning" };
  }
  if (attempts > 0 && retryShare >= 0.3) {
    return { label: "failing, low volume", tone: "info" };
  }
  if (attempts === 0 && depth === 0) {
    return { label: "idle", tone: "neutral" };
  }

  return { label: "healthy", tone: "ok" };
};

/**
 * The non-OK Cloud Tasks response codes behind a queue's failures, worst
 * first. This is the field that turns "retry share 100%" from a mystery into
 * something actionable — `UNAVAILABLE` (target down) and `NOT_FOUND` (bad
 * handler path) call for completely different fixes.
 */
export const failureCodes = (attemptsByCode = {}, limit = 3) =>
  Object.entries(attemptsByCode)
    .filter(([code]) => code !== "OK")
    .map(([code, count]) => ({ code, count: Math.round(count || 0) }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
